import React from 'react';
import { select, t, Trans } from '@lingui/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import { Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import LandingSearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/LandingSearchResultsCarousel';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';

import { getSearchFiltersFromSeoLocationDetails } from '../../../utilities';
import {
  RoverProtectCondensed,
  SeoLocationFooter,
  SeoLocationHero,
  SeoLocationPageWrapper,
  ValuePropFilterOptions,
  WhyChooseRover,
} from '../../components';
import { DoggyDayCareFaqs } from '../../components/Footer/ServiceFaqs';
import useFacts from '../../components/Footer/ServiceFaqs/useFacts';
import QuickFact, { QuickFactHighlight } from '../../components/QuickFact';
import SeoLocationReviewsCarousel from '../../components/SeoLocationReviewsCarousel';
import { PageSectionId, useSectionRef } from '../../hooks/useSectionRef';
import { dogBoardingHeroImage, quickFactBackgroundDoodle } from '../../images';
import SeoLocationPage, { SeoLocationPageProps } from '../../types';
import onValuePropFilterOptionsCtaClick from '../../utils/onValuePropFilterOptionsCtaClick';

export const DoggyDayCare: SeoLocationPage = ({
  seoLocation,
  reviews,
  countryConfig,
  frontendConfig,
  serviceTypes,
  searchResponse,
  topNeighborhoodServices,
}: SeoLocationPageProps) => {
  const { language } = frontendConfig.settings;
  const { localityType, details: seoLocationDetails, linkedLocations } = seoLocation;
  const { serviceTypeSlug, facts, name: locationName } = seoLocationDetails;
  const initialSearchFilters = getSearchFiltersFromSeoLocationDetails(seoLocation);
  const { localizedLinkUrls } = camelize(countryConfig.urls, true);

  const whyChooseRoverRef = useSectionRef(PageSectionId.WhyChooseRover);
  const faqsRef = useSectionRef(PageSectionId.FAQs);
  const howBrandWorksRef = useSectionRef(PageSectionId.HowBrandWorks);

  const {
    totalCustomersNum,
    medianListPricePerUnitLocalCurrencyNum,
    avgRepeatCustomerAmongSittersWithRepeatNum,
  } = useFacts(facts);

  // tokens for translations
  const cityName = locationName;
  const neighborhoodName = locationName;

  return (
    <SeoLocationPageWrapper seoLocation={seoLocation} countryConfig={countryConfig}>
      <Section>
        <Flex flexDirection="column" alignItems="center">
          <SeoLocationHero
            heading={seoLocationDetails.h1}
            subheading={seoLocationDetails.h2}
            desktopImage={dogBoardingHeroImage}
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
        </Flex>
      </Section>

      <RoverProtectCondensed countryConfig={countryConfig} my={['6x', '12x']} />

      <Section paddingLeft="4x" mb={['16x', '12x']}>
        <LandingSearchResultsCarousel
          searchResponse={searchResponse}
          searchFilters={initialSearchFilters}
          language={language}
          heading={select(localityType, {
            city: `Top rated ${cityName} doggy day care sitters`,
            neighborhood: `Top rated ${neighborhoodName} doggy day care sitters`,
            other: `Top rated ${locationName} doggy day care sitters`,
          })}
          headingVariant="display"
          serviceTypeSlug={serviceTypeSlug}
          countryConfig={countryConfig}
        />
      </Section>

      {totalCustomersNum &&
        (() => {
          const { asNumber: ownerCount } = totalCustomersNum;
          return (
            <QuickFact stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                Over <QuickFactHighlight>{ownerCount}</QuickFactHighlight> pet parents in {cityName}{' '}
                have booked doggy day care
              </Trans>
            </QuickFact>
          );
        })()}

      <SeoLocationReviewsCarousel
        seoLocation={seoLocation}
        reviews={reviews}
        searchFilters={initialSearchFilters}
        language={language}
      />

      <ValuePropFilterOptions
        locationName={locationName}
        language={language}
        searchFilters={initialSearchFilters}
        acceptsPuppies
        onPress={onValuePropFilterOptionsCtaClick(serviceTypeSlug)}
      />

      <HowBrandWorks htmlRef={howBrandWorksRef} mt={['1x', '2x']} mb={['1x', '3x']}>
        <HowBrandWorks.Display>
          <Trans>How Rover works</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          {[
            {
              heading: t`Search`,
              content: t`Read verified reviews by dog parents like you and choose a sitter who’s a great match for you and your dogs`,
            },
            {
              heading: t`Meet & Greet`,
              content: t`Meet either virtually or in-person with a sitter to discuss the level of care needed for your dogs while you’re away`,
            },
            {
              heading: t`Book & Pay`,
              content: t`All payments are processed through Rover’s secured platform and your booking is backed by 24/7 support. It’s that easy!`,
            },
          ].map((item, index) => (
            <HowBrandWorks.ListItem key={index + 1}>
              <HowBrandWorks.ItemHeading step={index + 1}>{item.heading}</HowBrandWorks.ItemHeading>
              <HowBrandWorks.ItemContent>{item.content}</HowBrandWorks.ItemContent>
            </HowBrandWorks.ListItem>
          ))}
        </HowBrandWorks.List>
      </HowBrandWorks>

      {medianListPricePerUnitLocalCurrencyNum &&
        (() => {
          const { asCurrency: formattedAmount } = medianListPricePerUnitLocalCurrencyNum;
          return (
            <QuickFact stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                The median cost per day for doggy day care in {cityName} is{' '}
                <QuickFactHighlight>{formattedAmount}</QuickFactHighlight>
              </Trans>
            </QuickFact>
          );
        })()}

      <WhyChooseRover
        htmlRef={whyChooseRoverRef}
        reservationProtection={t`It doesn’t happen often, but if your dog sitter has to cancel, we’ll work with you to find a new sitter.`}
        supportText={t`We’re friendly, knowledgable, and total dog fanatics.`}
        supportUrl={countryConfig.urls.supportUrl}
      />
      {avgRepeatCustomerAmongSittersWithRepeatNum &&
        (() => {
          const { asNumber: clientCount } = avgRepeatCustomerAmongSittersWithRepeatNum;
          return (
            <QuickFact stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                Sitters in {cityName} average <QuickFactHighlight>{clientCount}</QuickFactHighlight>{' '}
                repeat doggy day care clients
              </Trans>
            </QuickFact>
          );
        })()}
      <Section>
        <DoggyDayCareFaqs
          facts={facts}
          locationName={locationName}
          localityType={localityType}
          localizedLinkUrls={localizedLinkUrls}
          htmlRef={faqsRef}
        />
        <SeoLocationFooter
          countryConfigurationUrls={countryConfig.urls}
          seoLocation={seoLocationDetails}
          linkedLocations={linkedLocations}
          topNeighborhoodServices={topNeighborhoodServices}
        />
      </Section>
    </SeoLocationPageWrapper>
  );
};

export default DoggyDayCare;
